import { useEffect, useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import { IReuniao, IReuniaoInterna, StatusReuniaoEnum } from '../../../commons/types/crm';
import { createTheme, ThemeProvider } from '@mui/material';
import { Box, Button, Card, CardContent, Chip, Divider, Grid, IconButton, InputAdornment, Pagination, Stack, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { esES, ptBR } from '@mui/material/locale';
import { DataGrid, GridColDef, GridSlots, type GridCellParams } from '@mui/x-data-grid';
import { useReuniaoInternaActions } from '../../../states/reunioesInternas/reunioesInternas.actions';
import moment from 'moment';
import DrawerReuniaoInterna from './DrawerReuniaoInterna';
import { GridToolbarContainer } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import dayjs from 'dayjs';
import { useTranslation } from '../../../commons/useTranslasion';
import { useDateFormatters } from '../../../utils/useDateFormatters';

interface AddToolbarProps {
  openAdd: Function;
}

function AddToolbar(props: AddToolbarProps) {
  const { translate } = useTranslation();
  const { openAdd } = props;

  const handleClick = () => {
    openAdd();
  };

  return (
    <GridToolbarContainer>
      <Button color="primary" startIcon={<AddIcon />} onClick={handleClick}>
        {translate('adicionarNovaReuniao')}
      </Button>
    </GridToolbarContainer>
  );
}

const ReunioesInternas: React.FC = () => {
  const { translate, language } = useTranslation();
  const { formatarData } = useDateFormatters();
  const lang = language ?? "pt-br";
  let locale = ptBR;
  switch (lang.slice(0, 2)) {
    case "es":
      locale = esES
      break;
  }
  
  const [open, setOpen] = useState(false);
  const [reunioes, setReunioes] = useState<IReuniaoInterna[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingSave, setLoadingSave] = useState(false);
  const [reuniao, setReuniao] = useState<IReuniaoInterna>();
  const actions = useReuniaoInternaActions();

  useEffect(() => {
    actions
      .list()
      .then((resp: IReuniaoInterna[]) => setReunioes(resp))
      .catch((e) => toast.error(e))
      .finally(() => setLoading(false));
  }, []);

  const columns: GridColDef[] = [
    {
      field: 'descricao',
      headerName: translate('descricao'),
      width: 220,
      flex: 220,
    },
    {
      field: 'data',
      headerName: translate('data'),
      width: 100,
      flex: 100,
      valueGetter(value: any, row: IReuniao) {
        const reuniao = row as IReuniao;
        if (
          reuniao.status === StatusReuniaoEnum.PENDENTE_APROVACAO ||
          reuniao.status === StatusReuniaoEnum.APROVADA
        )
          return formatarData(reuniao.dtRealizadaInicio);
        return formatarData(reuniao.dtPrevista);
      },
      // Extrai a data real (timestamp) para ordenação correta
      sortComparator: (v1, v2, param1, param2) => {
        const row1 = param1.api.getRow(param1.id) as IReuniao;
        const row2 = param2.api.getRow(param2.id) as IReuniao;
        
        const date1 = row1.status === StatusReuniaoEnum.PENDENTE_APROVACAO || 
                     row1.status === StatusReuniaoEnum.APROVADA
            ? new Date(row1.dtRealizadaInicio!).getTime()
            : new Date(row1.dtPrevista!).getTime();
            
        const date2 = row2.status === StatusReuniaoEnum.PENDENTE_APROVACAO || 
                     row2.status === StatusReuniaoEnum.APROVADA
            ? new Date(row2.dtRealizadaInicio!).getTime()
            : new Date(row2.dtPrevista!).getTime();
            
        return date1 - date2;
      },
    },
    {
      field: 'horaInicio',
      headerName: translate('inicio'),
      width: 100,
      flex: 100,
      valueGetter(params: any, row: IReuniao) {
        const reuniao = row;
        return dayjs(reuniao.dtRealizadaInicio!).format('HH:mm');
      },
    },
    {
      field: 'horaFim',
      headerName: translate('fim'),
      width: 100,
      flex: 100,
      valueGetter(params: any, row: IReuniao) {
        const reuniao = row;
        return dayjs(reuniao.dtRealizadaFim).format('HH:mm');
      },
    },
    {
      field: 'valorHora',
      headerName: translate('valorHora'),
      width: 120,
      flex: 120,
      renderCell: (params: GridCellParams) => {
        let obj = params.row as IReuniao;
        return (
          <> {obj.valorHora ? `${translate('prefixoMoeda')} ${parseFloat(obj.valorHora!.toString()).toFixed(2)}` : ''} </>
        );
      },
    },
    {
      field: 'statusValidacao',
      headerName: translate('status'),
      width: 160,
      flex: 160,
    },
  ];

  const openAdd = () => {
    setReuniao(undefined);
    setOpen(true);
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [filtro, setFiltro] = useState('');
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [mostrarTodos, setMostrarTodos] = useState(false);
  const itensPorPagina = 10; // Quantidade de cards por página

  // Filtragem de reuniões com base no texto digitado
  const reunioesFiltradas = useMemo(() => {
    if (!filtro.trim()) return reunioes;
    
    const termoLower = filtro.toLowerCase();
    return reunioes.filter(reuniao => 
      reuniao.descricao?.toLowerCase().includes(termoLower)
    );
  }, [reunioes, filtro]);
  
  // Cálculo para paginação
  const totalPaginas = Math.ceil(reunioesFiltradas.length / itensPorPagina);
  const reunioesPaginadas = useMemo(() => {
    // Se a opção "mostrar todos" estiver ativada, retorna todos os itens
    if (mostrarTodos) {
      return reunioesFiltradas;
    }
    
    // Caso contrário, aplica a paginação normal
    const inicio = (paginaAtual - 1) * itensPorPagina;
    const fim = inicio + itensPorPagina;
    return reunioesFiltradas.slice(inicio, fim);
  }, [reunioesFiltradas, paginaAtual, itensPorPagina, mostrarTodos]);
  
  // Resetar página quando o filtro muda ou quando alterna entre mostrar todos/paginar
  useEffect(() => {
    setPaginaAtual(1);
  }, [filtro, mostrarTodos]);
  
  // Função para trocar de página com rolagem suave ao topo
  const handleChangePage = (event: React.ChangeEvent<unknown>, value: number) => {
    setPaginaAtual(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Função para determinar a cor do chip de status (padronizado para primary)
  const getStatusColor = (statusText: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    // Sempre retorna primary para padronização e facilitar adaptação para whitelabel
    return "primary";
  };

  // Renderização mobile com cards
  const renderMobileCards = () => {
    return (
      <Box sx={{ py: 1, px: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap', mx: 1 }}>
          <Button
            color="primary"
            variant="contained" 
            size="small"
            startIcon={<AddIcon />}
            onClick={openAdd}
            sx={{ borderRadius: '8px' }}
          >
            {translate('adicionarNovaReuniao')}
          </Button>

          {/* Campo de busca para filtragem */}
          <TextField
            variant="outlined"
            size="small"
            placeholder={translate('buscar')}
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            sx={{ 
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
              },
              minWidth: '180px',
              flexGrow: 1
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {reunioesFiltradas.length === 0 && (
          <Box sx={{ py: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {translate('carregando')}
            </Typography>
          </Box>
        )}

        {reunioesPaginadas.map((reuniao) => {
          // Obter o status diretamente do statusValidacao (mesma coluna da tabela desktop)
          const statusText = reuniao.statusValidacao || '';
          const statusColor = getStatusColor(statusText);

          // Formatação de data e hora
          const data = reuniao.status === StatusReuniaoEnum.PENDENTE_APROVACAO || 
                      reuniao.status === StatusReuniaoEnum.APROVADA
            ? formatarData(reuniao.dtRealizadaInicio)
            : formatarData(reuniao.dtPrevista);
          
          const horaInicio = dayjs(reuniao.dtRealizadaInicio).format('HH:mm');
          const horaFim = dayjs(reuniao.dtRealizadaFim).format('HH:mm');
          const valorFormatado = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(reuniao.valorHora || 0);
          
          return (
            <Card 
              key={reuniao.id} 
              sx={{ 
                mb: 2, 
                mx: 1,
                borderRadius: '10px',
                boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.08)'
              }}
            >
              <CardContent sx={{ px: 1.5, py: 1.5 }}>
                {/* Cabeçalho do card com a descrição */}
                <Typography 
                  variant="subtitle1" 
                  component="div" 
                  sx={{ 
                    fontWeight: 'bold', 
                    mb: 1,
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    maxWidth: '100%'
                  }}
                >
                  {reuniao.descricao}
                </Typography>
                
                {/* Status em chip */}
                <Box sx={{ mb: 1.5 }}>
                  <Chip 
                    size="small" 
                    label={statusText}
                    color={statusColor}
                    sx={{ 
                      fontSize: '0.75rem',
                      maxWidth: '100%',
                      height: 'auto',
                      '& .MuiChip-label': { 
                        whiteSpace: 'normal',
                        overflow: 'hidden',
                        padding: '4px 8px'
                      } 
                    }}
                  />
                </Box>
                
                <Divider sx={{ my: 1 }} />
                
                {/* Detalhes em colunas */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                  {/* Coluna 1 */}
                  <Box>
                    <Typography variant="caption" color="text.secondary" component="div" sx={{ fontWeight: 'bold' }}>
                      {translate("data")}
                    </Typography>
                    <Typography variant="body2" component="div">
                      {data}
                    </Typography>
                    
                    <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1, fontWeight: 'bold' }}>
                      {translate("valorHora")}
                    </Typography>
                    <Typography variant="body2" component="div">
                      {valorFormatado}
                    </Typography>
                  </Box>
                  
                  {/* Coluna 2 */}
                  <Box>
                    <Typography variant="caption" color="text.secondary" component="div" sx={{ fontWeight: 'bold' }}>
                      {translate("inicio")}/{translate("fim")}
                    </Typography>
                    <Typography variant="body2" component="div" sx={{ wordBreak: 'break-word' }}>
                      {horaInicio} - {horaFim}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          );
        })}
        {/* Paginação e opção de mostrar todos */}
        {(totalPaginas > 1 || mostrarTodos) && (
          <Stack spacing={1} sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
            {/* Controles de paginação visíveis apenas quando não está mostrando todos */}
            {!mostrarTodos && <Pagination count={totalPaginas} page={paginaAtual} onChange={handleChangePage} color="primary" size="medium" siblingCount={0} />}
            
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', maxWidth: '400px', mt: 1 }}>
              {/* Texto informativo sobre a página atual ou total de itens */}
              <Typography variant="caption" color="text.secondary">
                {!mostrarTodos 
                  ? `${translate('pagina')} ${paginaAtual} ${translate('de')} ${totalPaginas}` 
                  : `${translate('exibindo')} ${reunioesFiltradas.length} ${translate('itens')}`
                }
              </Typography>
              
              {/* Botão para alternar entre visualização paginada e completa */}
              <Button 
                size="small" 
                color="primary" 
                variant="text"
                onClick={() => setMostrarTodos(!mostrarTodos)}
                sx={{ fontSize: '0.75rem', minWidth: 'auto' }}
              >
                {mostrarTodos ? translate('mostrarPaginado') : translate('mostrarTodos')}
              </Button>
            </Box>
          </Stack>
        )}
      </Box>
    );
  };

  return (
    <>
      <ThemeProvider theme={createTheme({}, locale)}>
        {isMobile ? (
          // Visualização mobile com cards
          renderMobileCards()
        ) : (
          // Visualização desktop mantém a tabela original
          <DataGrid
            rows={reunioes}
            loading={loading}
            columns={columns}
            slots={{ toolbar: () => <AddToolbar openAdd={openAdd} /> }}
            sx={{
              borderRadius: '10px',
              minHeight: '700px',
              height: 'auto',
              width: '100%',
              '& .MuiDataGrid-main': {
                width: '100%'
              },
              '& .MuiDataGrid-virtualScroller': {
                width: '100%'
              },
              '& .MuiDataGrid-columnsContainer': {
                width: '100%'
              },
              '& .MuiDataGrid-cell': {
                maxWidth: 'none !important'
              }
            }}
          />
        )}
      </ThemeProvider>
      <DrawerReuniaoInterna
        reuniao={reuniao}
        open={open}
        setOpen={setOpen}
        loadingSave={loadingSave}
        setLoadingSave={setLoadingSave}
        setReunioes={setReunioes}
      />
    </>
  );
};

export default ReunioesInternas;
